import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Page, BrandHeader, CloseBtn } from "@/components/Brand";
import { Btn } from "@/components/Btn";
import { NeedPicker, NEED_UNSURE } from "@/components/NeedPicker";
import { SchemaRecognizer } from "@/components/SchemaRecognizer";
import { FlipPlan } from "@/components/FlipPlan";
import { NeedAssistant } from "@/components/NeedAssistant";
import { NeedIdeas } from "@/components/NeedIdeas";
import { suggestSchemas } from "@/lib/schemaSuggest";
import { needStatements } from "@/lib/schemaFlip";
import { Spinner } from "@/components/Brand";
import { emptyFlip } from "@/lib/schemaFlip";
import { healthyWins, healthyWinMessage } from "@/lib/healthyWins";
import { useFlips, useSaveFlip } from "@/lib/store";

export default function FlipTool() {
  const navigate = useNavigate();
  const save = useSaveFlip();
  const { data: allFlips } = useFlips();
  const [step, setStep] = useState("safe");
  const [needs, setNeeds] = useState([]);
  const [needIdeas, setNeedIdeas] = useState([]);
  const [sugg, setSugg] = useState({ schemas: [], reasons: {} });
  const [suggLoading, setSuggLoading] = useState(false);
  const toRecognize = async () => {
    setStep("recognize");
    setSuggLoading(true);
    setSugg(await suggestSchemas({ "Where they are": "Using the Flip the Script tool", "Unmet needs they ticked": needStatements(needs) }));
    setSuggLoading(false);
  };
  const [recognized, setRecognized] = useState([]);
  const [flips, setFlips] = useState([]);
  const close = () => navigate(-1);

  const toFlip = () => {
    setFlips(recognized.map((r) => ({ ...(flips.find((f) => f.schema === r.schema) ?? emptyFlip(r.schema)), copingStyle: r.copingStyle })));
    setStep("flip");
  };
  const onSave = async () => {
    const entry = await save.mutateAsync({ source: "tool", sessionId: null, needs: needs.filter((n) => n !== NEED_UNSURE), needIdeas, flips });
    toast.success(flips.some((f) => f.copingStyle === "healthy") ? healthyWinMessage(healthyWins([...(allFlips ?? []), entry])) : "Your flip is saved");
    navigate("/history", { replace: true });
  };

  const heads = {
    safe: ["Somatic safety check", "Am I safe enough to reflect right now?", "When the body is in threat mode, thinking work can wait. Calm your nervous system first, and come back when you're ready."],
    need: ["Core need", "Which needs aren't being met?", "Describe what you need in your own words and AI will help you name it, or open each topic and tick every statement that feels true."],
    recognize: ["Recognise the schema", null, "Does any of these sound like your inner voice? Tap the ones that fit, then choose what you did."],
    flip: ["Flip the Script", null, "Your Healthy Adult plan, moving from the old script to a new one."],
  };
  const [tag, q, help] = heads[step];

  return (
    <div className="min-h-screen">
      <Page className="!pb-40">
        <BrandHeader right={<CloseBtn onClick={close} testId="flip-close" />} />
        <div className="mt-10 tf-rise" key={step}>
          <p className="tf-tag">{tag}</p>
          {q ? <h1 className="mt-2 text-3xl sm:text-4xl font-bold text-t-on">{q}</h1> : null}
          <p className="mt-3 text-t-on2">{help}</p>
          {step === "safe" ? (
            <div className="mt-8 flex flex-col gap-3">
              <Btn label="Yes, I'm safe enough" onClick={() => setStep("need")} testId="flip-safe-yes" />
              <Btn variant="secondary" label="No, calm my body first" onClick={() => navigate("/grounding", { replace: true })} testId="flip-safe-no" />
            </div>
          ) : null}
          {step === "need" ? <><NeedAssistant context={{ "Where they are": "Using the Flip the Script tool to reality-test a schema outside of a check-in" }} autoSuggest={false} value={needs} onChange={setNeeds} /><NeedPicker value={needs} onChange={setNeeds} /></> : null}
          {step === "recognize" ? (suggLoading
            ? <div className="mt-6 flex items-center gap-3 text-t-on2"><Spinner className="text-t-brand" /> Finding the schemas that best fit your needs…</div>
            : <SchemaRecognizer needs={needs} suggested={sugg.schemas} reasons={sugg.reasons} value={recognized} onChange={setRecognized} />) : null}
          {step === "flip" ? <><FlipPlan flips={flips} onChange={setFlips} needs={needs} /><div className="mt-5"><NeedIdeas needs={needs} context={{
            "Where they are": "Using the Flip the Script tool",
            "Schemas they are flipping": flips.map((f) => f.schema),
            "The stories their schemas told": flips.map((f) => f.story).filter(Boolean),
            "The facts they wrote": flips.map((f) => f.facts).filter(Boolean),
            "Small opposite actions chosen": flips.map((f) => f.action).filter(Boolean),
          }} value={needIdeas} onChange={setNeedIdeas} /></div></> : null}
        </div>
      </Page>
      {step !== "safe" ? (
        <div className="fixed bottom-0 inset-x-0 bg-t-surface/90 backdrop-blur-xl border-t border-t-border">
          <div className="mx-auto max-w-2xl px-5 sm:px-8 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {step === "need" ? <Btn className="w-full" label="Continue" disabled={!needs.length} onClick={toRecognize} testId="flip-need-continue" /> : null}
            {step === "recognize" ? <Btn className="w-full" label="Flip the script" disabled={!recognized.length} onClick={toFlip} testId="flip-recognize-continue" /> : null}
            {step === "flip" ? <Btn className="w-full" label="Save" onClick={onSave} loading={save.isPending} testId="flip-save" /> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
